// Discovery of the tenant's callout services: one discovery scatter under the
// callout service's name, as the library's inference discovery scatters under
// the inference service's (02-DESIGN/05-inference/02-the-inference-contract.md
// § the content callout; decision 0040 § 1). The library at the version the
// console pins has no discovery for this service name, so the console does it
// here, with the library's own attestation check (decision 0026): an answer
// the platform does not vouch for its name is dropped, uncounted. The console
// only says whether a callout of a name answers; the instance discovers and
// vouches it again before every request it makes.

import { createInbox, type Msg, type NatsConnection } from "@nats-io/nats-core";
import { identity, subject } from "@impire-io/sdk";

import { CALLOUT_SERVICE, META_CALLOUT_NAME } from "../wire/inference.ts";

/** Callout is one callout service as discovery shows it: its registration name and its workload. */
export interface Callout {
  name: string;
  workload: string;
}

/** The callout endpoint's tokens after the workload id: `workload.<id>.callout.content`. */
const TOKEN_CALLOUT = "callout";
const TOKEN_CONTENT = "content";

/** calloutWorkload reads the workload id back from a callout service's endpoint subject. */
export function calloutWorkload(subj: string): string | undefined {
  let toks: string[];
  try {
    toks = subject.address(subj);
  } catch {
    return undefined;
  }
  const [family, id, callout, content] = toks;
  if (
    toks.length !== 4 ||
    family !== subject.Family.workload ||
    callout !== TOKEN_CALLOUT ||
    content !== TOKEN_CONTENT ||
    id === undefined ||
    !subject.validToken(id)
  ) {
    return undefined;
  }
  return id;
}

/** parseCalloutInfo reads one discovery answer: the name its metadata advertises, the workload its endpoint names, the metadata the attestation is checked in; undefined for anything else. */
export function parseCalloutInfo(
  data: Uint8Array,
): { callout: Callout; metadata: Record<string, unknown> } | undefined {
  let info: unknown;
  try {
    info = JSON.parse(new TextDecoder().decode(data));
  } catch {
    return undefined;
  }
  if (typeof info !== "object" || info === null) {
    return undefined;
  }
  const o = info as Record<string, unknown>;
  if (o.name !== CALLOUT_SERVICE) {
    return undefined;
  }
  const metadata =
    typeof o.metadata === "object" && o.metadata !== null ? (o.metadata as Record<string, unknown>) : {};
  const name = typeof metadata[META_CALLOUT_NAME] === "string" ? metadata[META_CALLOUT_NAME] : "";
  let workload: string | undefined;
  if (Array.isArray(o.endpoints)) {
    for (const ep of o.endpoints as unknown[]) {
      if (
        typeof ep === "object" &&
        ep !== null &&
        typeof (ep as { subject?: unknown }).subject === "string"
      ) {
        workload = calloutWorkload((ep as { subject: string }).subject);
        if (workload !== undefined) {
          break;
        }
      }
    }
  }
  if (name === "" || workload === undefined) {
    return undefined;
  }
  return { callout: { name, workload }, metadata };
}

/** inboxPrefixOf reads the inbox prefix the connection was placed with, as the library does: the fence a reply must land inside. */
function inboxPrefixOf(nc: NatsConnection): string | undefined {
  const options: unknown = (nc as { options?: unknown }).options;
  if (typeof options === "object" && options !== null && "inboxPrefix" in options) {
    const prefix: unknown = options.inboxPrefix;
    if (typeof prefix === "string" && prefix !== "") {
      return prefix;
    }
  }
  return undefined;
}

/** The server's no-responders status, delivered to the reply inbox when nothing is subscribed. */
const NO_RESPONDERS = 503;

/** nextWithin waits for a message for at most ms; undefined when none came. */
async function nextWithin(it: AsyncIterator<Msg>, ms: number): Promise<Msg | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      it.next(),
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => {
          resolve(undefined);
        }, ms);
      }),
    ]);
    if (result === undefined || result.done === true) {
      return undefined;
    }
    return result.value;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * discoverCallouts scatters the callout service's discovery on the caller's own
 * connection and answers every callout service the platform vouches for its
 * name in tenant — until the window closes with no answer, the quiet period
 * passes after the last one, or the server says nobody is subscribed.
 * Nothing is cached.
 */
export async function discoverCallouts(
  nc: NatsConnection,
  trust: { keys: identity.KeySet; tenant: string },
  opts: { window?: number; quiet?: number } = {},
): Promise<Callout[]> {
  const inbox = createInbox(inboxPrefixOf(nc));
  const sub = nc.subscribe(inbox);
  const it = sub[Symbol.asyncIterator]();
  const out: Callout[] = [];
  const seen = new Set<string>();
  try {
    nc.publish(subject.serviceInfo(CALLOUT_SERVICE), undefined, { reply: inbox });
    let wait = opts.window ?? 1000;
    for (;;) {
      const msg = await nextWithin(it, wait);
      if (msg === undefined || (msg.headers?.code ?? 0) === NO_RESPONDERS) {
        return out;
      }
      const got = parseCalloutInfo(msg.data);
      if (
        got !== undefined &&
        !seen.has(got.callout.workload) &&
        (await identity.vouched(
          got.metadata,
          trust.keys,
          trust.tenant,
          got.callout.name,
          got.callout.workload,
        ))
      ) {
        seen.add(got.callout.workload);
        out.push(got.callout);
      }
      wait = opts.quiet ?? 100;
    }
  } finally {
    sub.unsubscribe();
  }
}
